import Image from "next/image";
import Icon from "@/components/icon";
import FooterDirectoryBadges from "@/components/mirofish/footer-directory-badges";
import { mirofishFooter } from "@/content/mirofish";
import { SUPPORT_EMAIL } from "@/lib/brand";

export default function MiroFishSiteFooter() {
  const currentYear = new Date().getFullYear();
  const footer = mirofishFooter;

  return (
    <footer
      className="border-t border-white/10 bg-black/40"
      data-testid="mirofish-site-footer"
    >
      <div className="mx-auto max-w-7xl px-4 py-12 text-sm text-white/65 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-10 text-center lg:flex-row lg:items-start lg:text-left">
          <div className="flex w-full max-w-xl shrink flex-col items-center gap-6 lg:items-start">
            {footer.brand ? (
              <div>
                <a
                  href={footer.brand.url || "/"}
                  target={footer.brand.target}
                  className="flex items-center justify-center gap-3 lg:justify-start"
                >
                  {footer.brand.logo?.src ? (
                    <Image
                      src={footer.brand.logo.src}
                      alt={footer.brand.logo.alt || footer.brand.title || "MiroFish"}
                      width={48}
                      height={48}
                      sizes="48px"
                      className="h-12 w-12 rounded-2xl object-cover"
                      data-brand-logo="footer"
                    />
                  ) : null}
                  {footer.brand.title ? (
                    <span className="text-3xl font-semibold tracking-[0.08em] text-white">
                      {footer.brand.title}
                    </span>
                  ) : null}
                </a>
                {footer.brand.description ? (
                  <p className="mt-6 max-w-xl text-base leading-7 text-white/60">
                    {footer.brand.description}
                  </p>
                ) : null}
              </div>
            ) : null}

            {footer.social?.items?.length ? (
              <ul className="flex items-center gap-5 text-white/48">
                {footer.social.items.map((item, index) => (
                  <li key={`${item.title}-${index}`}>
                    <a
                      href={item.url || "#"}
                      target={item.target}
                      aria-label={item.title}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
                    >
                      {item.icon ? <Icon name={item.icon} className="size-4" /> : null}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:gap-12">
            {footer.nav?.items?.map((item, index) => (
              <div key={`${item.title}-${index}`}>
                <p className="mb-6 font-semibold uppercase tracking-[0.18em] text-white/40">
                  {item.title}
                </p>
                <ul className="space-y-4 text-sm text-white/62">
                  {item.children?.map((child, childIndex) => (
                    <li key={`${child.title}-${childIndex}`}>
                      <a
                        href={child.url || "#"}
                        target={child.target}
                        className="font-medium transition hover:text-white"
                      >
                        {child.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 border-t border-white/10 pt-6 text-center lg:text-left">
          <p className="mb-2 text-sm text-white/70">
            support:{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="transition-colors hover:text-white"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
          {footer.disclaimer ? (
            <p className="max-w-4xl text-xs leading-6 text-white/45">
              {footer.disclaimer}
            </p>
          ) : null}
        </div>

        <FooterDirectoryBadges />

        <div className="mt-8 flex flex-col justify-between gap-4 border-t border-white/10 pt-8 text-center text-sm font-medium text-white/52 lg:flex-row lg:items-center lg:text-left">
          <p>
            © {currentYear} {footer.brand?.title}. {footer.copyright}
          </p>

          {footer.agreement?.items?.length ? (
            <ul className="flex flex-wrap justify-center gap-4 lg:justify-start">
              {footer.agreement.items.map((item, index) => (
                <li key={`${item.title}-${index}`}>
                  <a
                    href={item.url || "#"}
                    target={item.target}
                    className="transition hover:text-white"
                  >
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
